import { useMemo } from 'react'
import type { AppDispatch } from '@redux/store'

import { matchFilterArrayIncludes, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import * as providerActions from '@redux/modules/provider'

import type { EntityOptions } from '@redux/modules/types'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ProviderModel, ProviderRequestOptions } from '@models/types'

const watchEntityKeys = ['providers']

type LoadProviderIdsParams = {
  dispatch: AppDispatch,
  providerIds: number[],
  requestOptions?: EntityOptions,
}

const loadProviderIds = (params: LoadProviderIdsParams) => {
  const { dispatch, providerIds, requestOptions } = params
  const { loadProviderIds: loadFn } = providerActions

  // @ts-expect-error TS2769
  return dispatch(loadFn(providerIds, requestOptions))
}

type ProviderFilters = {
  assigned_brand_id?: number,
  string?: string,
}

type UseProvidersOptions = {
  filters?: ProviderFilters,
  performHttpRequests?: boolean,
  requestOptions?: ProviderRequestOptions,
}

function useProviders(options: UseProvidersOptions) {
  const { filters = {}, requestOptions } = options
  const {
    assigned_brand_id: filterAssignedBrandId,
    string: filterString,
  } = filters

  const {
    updatedEntities: { providers: providersUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const dispatch = useDispatch()

  const { providers } = useSelector(reduxState => reduxState.entities)
  const providerArray = Object.values(providers)

  // Filter redux entities with provided filters
  const filteredProviders = useMemo(() => {
    const filtered = providerArray.filter((provider: ProviderModel) => {
      const { assigned_brand_ids = [], id, title } = provider

      // @ts-expect-error TS18048
      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchAssignedBrandId = matchFilterArrayIncludes(assigned_brand_ids, filterAssignedBrandId)

      return matchAssignedBrandId && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [providersUpdatedAt, JSON.stringify(filters)])

  const filteredProvidersCount = filteredProviders.length
  const hasFilteredProviders = !!filteredProvidersCount

  // To be used in conjunction with DashboardModule.LoadMoreFooter
  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredProvidersCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // Load entities when performHttpRequests is true
  const reduxActionPayload = useReduxAction(
    'providers',
    'loadProviders',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  const { loading: loadingProviders, reduxActionPerformed } = reduxActionPayload

  return {
    callbacks: {
      loadMore,
      loadProviderIds: (params: { providerIds: number[], entityOptions?: EntityOptions }) => {
        const { providerIds, entityOptions } = params
        return loadProviderIds({ dispatch, providerIds, requestOptions: entityOptions })
      },
    },
    canLoadMore,
    filteredProviders,
    filteredProvidersCount,
    hasFilteredProviders,
    loading: loadingProviders,
    providerArray,
    providers,
    reduxActionPerformed,
  }
}

export default useProviders
